import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatIDR, formatDate, formatDayDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Truck, PackageCheck, MapPin, User, Search, UserCog, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Deliveries() {
  const [status, setStatus] = useState("Belum Dikirim");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [adminF, setAdminF] = useState("all");
  const [adminOptions, setAdminOptions] = useState([]);

  const load = () => api.get(`/deliveries?status=${encodeURIComponent(status)}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]);
  useEffect(() => { api.get("/deliveries/admins").then((r) => setAdminOptions(r.data.admins || [])).catch(() => {}); }, []);
  const q = search.trim().toLowerCase();
  const filtered = items
    .filter((t) => adminF === "all" || t.created_by_name === adminF)
    .filter((t) => !q || [t.order_no, t.customer_name, t.customer_phone, t.category, t.ucapan, t.dari, t.alamat, t.description]
      .some((v) => (v || "").toString().toLowerCase().includes(q)));

  const deliver = async (id) => {
    try { await api.patch(`/transactions/${id}/deliver`); toast.success("Ditandai sudah dikirim"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Titipan Belum Dikirim</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Pesanan yang sudah dibayar namun belum dikirim ke pelanggan.</p>
        </div>
        <div className="relative sm:max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="delivery-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari no. order / nama / telepon..."
            className="pl-9 h-9 text-sm"
          />
        </div>
        <Select value={adminF} onValueChange={setAdminF}>
          <SelectTrigger className="w-full sm:w-48 h-9 text-sm" data-testid="delivery-admin-filter">
            <UserCog className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
            <SelectValue placeholder="Semua Admin" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Admin</SelectItem>
            {adminOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="inline-flex gap-1 rounded-xl bg-secondary p-1">
          {["Belum Dikirim", "Sudah Dikirim"].map((s) => (
            <Button key={s} size="sm" variant={status === s ? "default" : "ghost"} onClick={() => setStatus(s)}
              data-testid={`delivery-filter-${s === "Belum Dikirim" ? "pending" : "done"}`}
              className={`h-8 px-3 text-xs font-medium ${status === s ? "shadow-sm" : "text-muted-foreground"}`}>{s}</Button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && (
        <Card className="p-10 text-center text-muted-foreground">
          <PackageCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
          {q
            ? "Tidak ada hasil yang cocok dengan pencarian."
            : status === "Belum Dikirim" ? "Tidak ada titipan yang menunggu pengiriman." : "Belum ada pesanan yang dikirim."}
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((t) => (
          <Card key={t._id} data-testid={`delivery-card-${t._id}`} className="p-4 flex flex-col">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300 text-[10px] px-1.5 py-0">{t.category}</Badge>
                {t.order_no && <span className="ml-1.5 text-[10px] font-semibold text-muted-foreground">{t.order_no}</span>}
                <p className="font-heading text-base font-extrabold mt-1.5">{formatIDR(t.amount)}</p>
              </div>
              <div className="grid place-items-center h-9 w-9 rounded-lg bg-amber-100 dark:bg-amber-950 shrink-0">
                <Truck className="h-4 w-4 text-amber-600" />
              </div>
            </div>
            <div className="mt-2.5 space-y-1 text-xs flex-1">
              {t.customer_name && (
                <p className="flex items-center gap-1.5 text-muted-foreground"><User className="h-3.5 w-3.5 shrink-0" />{t.customer_name}{t.customer_phone ? ` · ${t.customer_phone}` : ""}</p>
              )}
              <p className="flex items-center gap-1.5 text-muted-foreground" data-testid={`delivery-admin-${t._id}`}><UserCog className="h-3.5 w-3.5 shrink-0" />Admin: {t.created_by_name || "-"}</p>
              {t.supplier_name && (
                <p className="flex items-center gap-1.5 text-muted-foreground" data-testid={`delivery-supplier-${t._id}`}><Building2 className="h-3.5 w-3.5 shrink-0" />Vendor: {t.supplier_name}</p>
              )}
              {t.ucapan && <p className="text-muted-foreground whitespace-pre-line">Ucapan: {t.ucapan}</p>}
              {t.dari && <p className="text-muted-foreground whitespace-pre-line">Dari: {t.dari}</p>}
              <p className="flex items-start gap-1.5 text-muted-foreground"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span className="break-words whitespace-pre-line">{t.alamat || t.description || "-"}</span></p>
              <p className="text-[11px] text-muted-foreground pt-0.5">Tanggal order: {formatDate(t.date)}</p>
              <p data-testid={`delivery-date-${t._id}`} className="text-[11px] font-semibold text-rose-600 dark:text-rose-400">Tanggal kirim: {formatDayDate(t.delivery_date)}</p>
            </div>
            {status === "Belum Dikirim" && (
              <Button className="mt-3 w-full gap-1.5 h-8 text-xs" onClick={() => deliver(t._id)} data-testid={`deliver-btn-${t._id}`}>
                <PackageCheck className="h-3.5 w-3.5" /> Tandai Sudah Dikirim
              </Button>
            )}
            {status === "Sudah Dikirim" && t.delivered_at && (
              <p data-testid={`delivered-at-${t._id}`} className="mt-3 text-[11px] font-semibold text-rose-600 dark:text-rose-400 flex items-center gap-1"><PackageCheck className="h-3.5 w-3.5" /> Dikirim: {formatDayDate(t.delivered_at)}</p>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
